import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { Map, Marker, NavigationControl } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import Flatpickr from 'react-flatpickr';
import "flatpickr/dist/flatpickr.min.css";

// Mapbox access token
const MAPBOX_TOKEN = 'pk.eyJ1IjoidW5pdGVkc3lzdGVtYSIsImEiOiJjbWlsZWpnMXQwc2NlM2RzNW9wMWR4OWRwIn0.CP-OkFaPzS-Og8wtyZKleQ';

const CreateEvent = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [ticketingMode, setTicketingMode] = useState('tickets'); // 'tickets' | 'seatmap'
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [existingVideo, setExistingVideo] = useState(null);
    const [removeVideoFlag, setRemoveVideoFlag] = useState(false);
    // Track original values for payment check
    const [originalFeaturedEvent, setOriginalFeaturedEvent] = useState(false);
    const [originalTopEvent, setOriginalTopEvent] = useState(false);



    const formatDate = (date) =>
        date ? new Date(date).toISOString().slice(0, 19).replace('T', ' ') : '';

    const handleVideoSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('video/')) {
            toast.error('Please upload a valid video file');
            return;
        }

        setSelectedVideo(file);
        setRemoveVideoFlag(true); // mark the old video for deletion
        setExistingVideo(null); // clear old video preview
    };


    const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_category_id: '',
    location: '',
    latitude: '25.276987', // Doha, Qatar default
    longitude: '51.520008',
    event_start: '',
    event_end: '',
    sale_start: '',
    sale_end: '',
    currency: 'QAR',
    status: true,
    featured_event: false,
    top_event: false,
    video_link: ''
  });

    const [ticketCategories, setTicketCategories] = useState([
        {
            category_name: '',
            description: '',
            price: '',
            remaining_tickets: '',
            ceiling_limit: 10,
            open_to_public: true,
            type: 'ticket',
        }
    ]);
    const [valetEnabled, setValetEnabled] = useState(false);
    const [valetParking, setValetParking] = useState({
        category_name: 'Valet Parking',
        description: '',
        price: '',
        remaining_tickets: '',
        ceiling_limit: 10,
        open_to_public: true,
        type: 'valet',
    });

  const steps = [
    { number: 1, title: 'Basic Info', icon: 'fa-info-circle' },
    { number: 2, title: 'Location', icon: 'fa-map-marker-alt' },
    { number: 3, title: 'Tickets', icon: 'fa-ticket-alt' },
    { number: 4, title: 'Images', icon: 'fa-images' }
  ];

  useEffect(() => {
    fetchCategories();
    if (isEditMode) {
      fetchEventData();
    }
  }, [id]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/events/categories');
      setCategories(response.data.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    }
  };

    const getAddonPrice = () => {
        let total = 0;

        if (!isEditMode) {
            if (formData.featured_event) total += 250;
            if (formData.top_event) total += 500;
        } else {
            if (!originalFeaturedEvent && formData.featured_event) total += 250;
            if (!originalTopEvent && formData.top_event) total += 500;
        }

        return total;
    };

    const fetchEventData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/organizer/events/${id}`);
      const event = response.data.data;

      const formatDateTime = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toISOString().slice(0, 16);
      };

      setFormData({
        title: event.title || '',
        description: event.description || '',
        event_category_id: event.event_category_id || '',
        location: event.location || '',
        latitude: event.latitude || '25.276987',
        longitude: event.longitude || '51.520008',
        event_start: formatDateTime(event.event_start),
        event_end: formatDateTime(event.event_end),
        sale_start: formatDateTime(event.sale_start),
        sale_end: formatDateTime(event.sale_end),
        currency: event.currency || 'QAR',
        status: event.status,
        featured_event: event.featured_event || false,
        top_event: event.top_event || false,
        //video_link: event.video_link || ''
      });

        // Store original values for payment check
        setOriginalFeaturedEvent(event.featured_event || false);
        setOriginalTopEvent(event.top_event || false);

        if (event.video_link) {
            setExistingVideo(
                `${process.env.REACT_APP_API_URL.replace('/api', '')}/storage/${event.video_link}`
            );
        }

      if (event.ticket_categories && event.ticket_categories.length > 0) {
        const regular = event.ticket_categories.filter(t => t.type !== 'valet');
        const valet = event.ticket_categories.find(t => t.type === 'valet');
        if (regular.length > 0) {
          setTicketCategories(regular.map(ticket => ({
            id: ticket.id,
            type: ticket.type || 'ticket',
            category_name: ticket.category_name || '',
            description: ticket.description || '',
            price: ticket.price || '',
            remaining_tickets: ticket.remaining_tickets,
            ceiling_limit: ticket.ceiling_limit,
            open_to_public: ticket.open_to_public
          })));
        }
        if (valet) {
          setValetEnabled(true);
          setValetParking({
            id: valet.id,
            type: 'valet',
            category_name: valet.category_name || 'Valet Parking',
            description: valet.description || '',
            price: valet.price || '',
            remaining_tickets: valet.remaining_tickets,
            ceiling_limit: valet.ceiling_limit,
            open_to_public: valet.open_to_public,
          });
        }
      }

      if (event.images && event.images.length > 0) {
        setExistingImages(event.images);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching event:', error);
      toast.error('Failed to load event data');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

    const handleTicketChange = (index, field, value) => {
        const updatedTickets = [...ticketCategories];
        updatedTickets[index][field] = value;
        setTicketCategories(updatedTickets);
    };

  const addTicketCategory = () => {
    setTicketCategories([...ticketCategories, {
      name: '',
      description: '',
      price: '',
      quantity: '',
      max_per_order: 10,
      status: true
    }]);
  };

  const removeTicketCategory = (index) => {
    const updatedTickets = ticketCategories.filter((_, i) => i !== index);
    setTicketCategories(updatedTickets);
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const totalImages = existingImages.length + selectedImages.length + files.length;
    
    if (totalImages > 5) {
      toast.error('Maximum 5 images allowed per event');
      return;
    }

    const validFiles = files.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is larger than 10MB`);
        return false;
      }
      return true;
    });

    setSelectedImages([...selectedImages, ...validFiles]);

    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setSelectedImages(selectedImages.filter((_, i) => i !== index));
    setImagePreviews(imagePreviews.filter((_, i) => i !== index));
  };

  const removeExistingImage = async (imageId) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        await api.delete(`/organizer/events/${id}/images/${imageId}`);
        setExistingImages(existingImages.filter(img => img.id !== imageId));
        toast.success('Image deleted successfully');
      } catch (error) {
        console.error('Error deleting image:', error);
        toast.error('Failed to delete image');
      }
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.title || !formData.description || !formData.event_category_id) {
          toast.error('Please fill in all required fields');
          return false;
        }
        if (!formData.event_start || !formData.event_end) {
          toast.error('Please set event dates');
          return false;
        }
        return true;
      case 2:
        if (!formData.location) {
          toast.error('Please enter event location');
          return false;
        }
        return true;
      case 3:
        if (ticketingMode === 'seatmap') return true;
        for (let ticket of ticketCategories) {
          if (!ticket.category_name || !ticket.price || !ticket.remaining_tickets || !ticket.ceiling_limit) {
            toast.error('Please fill in all ticket details');
            return false;
          }
        }
        return true;
      case 4:
        // In edit mode, we allow saving without new images if there are existing ones
        // But we still want to show the images step
        if (!isEditMode && existingImages.length + selectedImages.length === 0) {
          toast.error('Please add at least one image');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('Next button clicked on step:', currentStep);
    
    if (validateStep(currentStep)) {
      const newStep = Math.min(currentStep + 1, 4);
      console.log(`Validation passed. Moving from step ${currentStep} to step ${newStep}`);
      setCurrentStep(newStep);
      // Scroll to top when changing steps
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      console.log('Validation failed for step:', currentStep);
    }
  };

  const prevStep = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const newStep = Math.max(currentStep - 1, 1);
    console.log(`Moving back from step ${currentStep} to step ${newStep}`);
    setCurrentStep(newStep);
    // Scroll to top when changing steps
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

    const handleSubmit = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        console.log('Form submitted on step:', currentStep);

        // Only allow submission on step 4
        if (currentStep !== 4) {
            toast.warning('Please complete all steps before submitting');
            return;
        }

        if (!validateStep(currentStep)) return;

        setLoading(true);

        try {
            // Check if payment is required
            let requiresPayment = false;

            if (isEditMode) {
                // For updates: check if featured or top event was toggled from false to true
                const featuredToggled = !originalFeaturedEvent && formData.featured_event;
                const topToggled = !originalTopEvent && formData.top_event;
                requiresPayment = featuredToggled || topToggled;
            } else {
                // For creation: check if featured or top event is enabled
                requiresPayment = formData.featured_event || formData.top_event;
            }

            if (requiresPayment) {
                // Create draft and initiate payment flow
                await handlePaymentFlow();
            } else {
                // Normal event creation/update flow
                await handleNormalSubmit();
            }

        } catch (error) {
            console.error('Error saving event:', error);
            toast.error(error.response?.data?.message || 'Failed to save event');
            setLoading(false);
        }
    };

    const handlePaymentFlow = async () => {
        try {
            // Step 1: Prepare event payload
            const eventPayload = {
                title: formData.title,
                description: formData.description,
                event_category_id: formData.event_category_id,
                location: formData.location,
                geo_location: `${formData.latitude},${formData.longitude}`,
                event_start: formData.event_start,
                event_end: formData.event_end,
                sale_start: formData.sale_start,
                sale_end: formData.sale_end,
                currency: formData.currency,
                status: formData.status ? 1 : 0,
                featured_event: formData.featured_event ? 1 : 0,
                top_event: formData.top_event ? 1 : 0,
                tickets: [
                    ...(ticketingMode === 'tickets' ? ticketCategories.map(ticket => ({
                        id: ticket.id || null,
                        type: 'ticket',
                        category_name: ticket.category_name,
                        description: ticket.description,
                        price: ticket.price,
                        remaining_tickets: ticket.remaining_tickets,
                        ceiling_limit: ticket.ceiling_limit,
                        open_to_public: ticket.open_to_public ? 1 : 0,
                    })) : []),
                    ...(valetEnabled && valetParking.price && valetParking.remaining_tickets ? [{
                        id: valetParking.id || null,
                        type: 'valet',
                        category_name: valetParking.category_name || 'Valet Parking',
                        description: valetParking.description,
                        price: valetParking.price,
                        remaining_tickets: valetParking.remaining_tickets,
                        ceiling_limit: valetParking.ceiling_limit,
                        open_to_public: 1,
                    }] : []),
                ],
                images: [],
                video_link: null,
                // Add edit mode flag and event ID
                is_update: isEditMode,
                event_id: isEditMode ? id : null,
                remove_video: (isEditMode && (removeVideoFlag || (!existingVideo && !selectedVideo))) ? 1 : 0
            };

            // Step 2: Initiate payment and get order
            const paymentResponse = await api.post('/organizer/payments/skipcash/initiate', {
                event_payload: eventPayload
            });

            if (!paymentResponse.data.success) {
                throw new Error('Failed to initiate payment');
            }

            const orderId = paymentResponse.data.order_id;

            // Step 3: Upload images to draft if any (new images)
            if (selectedImages.length > 0) {
                const formDataImages = new FormData();
                selectedImages.forEach((image, index) => {
                    formDataImages.append(`images[${index}]`, image);
                });

                await api.post(`/organizer/draft-orders/${orderId}/images`, formDataImages, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            }

            // Step 4: Upload video to draft if any
            if (selectedVideo) {
                const formDataVideo = new FormData();
                formDataVideo.append('video', selectedVideo);

                await api.post(`/organizer/draft-orders/${orderId}/video`, formDataVideo, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            }

            // Step 5: Redirect to payment page
            const payUrl = paymentResponse.data.pay_url;

            // Store order ID in sessionStorage for callback page
            sessionStorage.setItem('pendingOrderId', orderId);

            // Redirect to payment page
            window.location.href = payUrl;

        } catch (error) {
            console.error('Payment flow error:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const handleNormalSubmit = async () => {
        try {
            // Build FormData
            const formDataToSend = new FormData();

            Object.entries(formData).forEach(([key, value]) => {
                if (value !== null && value !== undefined) {
                    if (typeof value === 'boolean') {
                        formDataToSend.append(key, value ? 1 : 0);
                    } else {
                        formDataToSend.append(key, value);
                    }
                }
            });

            formDataToSend.append('status', formData.status ? 1 : 0);
            formDataToSend.append('featured_event', formData.featured_event ? 1 : 0);
            formDataToSend.append('top_event', formData.top_event ? 1 : 0);

            if (selectedVideo) {
                formDataToSend.append('video_link', selectedVideo);
            }

            if (isEditMode && (removeVideoFlag || (!existingVideo && !selectedVideo))) {
                formDataToSend.append('remove_video', '1');
            }

            formDataToSend.append('geo_location', `${formData.latitude},${formData.longitude}`);

            let eventId = id;

            // Step 1: create or update the event
            if (isEditMode) {
                formDataToSend.append('_method', 'PUT');
                await api.post(`/organizer/events/${id}`, formDataToSend, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            } else {
                const response = await api.post('/organizer/events', formDataToSend, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                eventId = response.data.data.id;
            }

            // Steps 2+: tickets and images — rollback new event on failure
            try {
                // Handle ticket categories (skipped when seat map mode)
                const allTickets = [
                    ...(ticketingMode === 'tickets' ? ticketCategories.map(t => ({ ...t, type: 'ticket' })) : []),
                    ...(valetEnabled && valetParking.price && valetParking.remaining_tickets
                        ? [{ ...valetParking, type: 'valet' }]
                        : []),
                ];

                for (const ticket of allTickets) {
                    const ticketData = {
                        event_id: eventId,
                        type: ticket.type || 'ticket',
                        category_name: ticket.category_name,
                        description: ticket.description,
                        price: ticket.price,
                        remaining_tickets: ticket.remaining_tickets,
                        ceiling_limit: ticket.ceiling_limit,
                        open_to_public: ticket.open_to_public ? 1 : 0,
                    };

                    if (ticket.id) {
                        await api.put(`/organizer/ticket-categories/${ticket.id}`, ticketData);
                    } else {
                        await api.post('/organizer/ticket-categories', ticketData);
                    }
                }

                // Handle image uploads
                if (selectedImages.length > 0) {
                    const formDataImages = new FormData();
                    selectedImages.forEach((image, index) => {
                        formDataImages.append(`images[${index}]`, image);
                    });

                    await api.post(`/organizer/events/${eventId}/images`, formDataImages, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    });
                }
            } catch (postCreateError) {
                // Rollback: delete the newly created event so nothing is left in a broken state
                if (!isEditMode && eventId) {
                    try { await api.delete(`/organizer/events/${eventId}`); } catch {}
                }
                throw postCreateError;
            }

            toast.success(isEditMode ? 'Event updated successfully' : 'Event created successfully');
            setTimeout(() => navigate('/organizer/events'), 100);

        } catch (error) {
            console.error('Error in normal submit:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    // Handle map click
  const handleMapClick = (event) => {
    const { lng, lat } = event.lngLat;
    setFormData(prev => ({
      ...prev,
      latitude: lat.toString(),
      longitude: lng.toString()
    }));
  };

  // Step 1: Basic Info
  const renderBasicInfo = () => (
    <div>
      <div className="row">
        <div className="col-12 mb-3">
          <label className="form-label font-weight-bold">
            <i className="fas fa-heading text-primary mr-2"></i>
            Event Titles *
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            className="form-control form-control-lg"
            placeholder="Enter your event title"
            required
          />
        </div>

        <div className="col-12 mb-3">
          <label className="form-label font-weight-bold">
            <i className="fas fa-align-left text-primary mr-2"></i>
            Description *
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            className="form-control"
            rows="6"
            placeholder="Describe your event in detail..."
            required
          />
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label font-weight-bold">
            <i className="fas fa-folder text-primary mr-2"></i>
            Category *
          </label>
          <select
            name="event_category_id"
            value={formData.event_category_id}
            onChange={handleInputChange}
            className="form-control"
            required
          >
            <option value="">Select Category</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.category_name}</option>
            ))}
          </select>
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label font-weight-bold">
            <i className="fas fa-dollar-sign text-primary mr-2"></i>
            Currency *
          </label>
          <select
            name="currency"
            value={formData.currency}
            onChange={handleInputChange}
            className="form-control"
            required
          >
            <option value="QAR">QAR - Qatari Riyal</option>
              <option value="AED">AED – United Arab Emirates Dirham</option>
              <option value="SAR">SAR – Saudi Riyal</option>
            <option value="USD">USD - US Dollar</option>
            <option value="EUR">EUR - Euro</option>
          </select>
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label font-weight-bold">
            <i className="fas fa-calendar-check text-success mr-2"></i>
            Event Start Date & Time *
          </label>
            <Flatpickr
                options={{
                    enableTime: true,
                    dateFormat: 'Y-m-d H:i',
                }}
                value={formData.event_start}
                onChange={([date]) =>
                    setFormData({ ...formData, event_start: formatDate(date) })
                }
                className="form-control"
                placeholder="Event start date & time"
                required
            />

        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label font-weight-bold">
            <i className="fas fa-calendar-times text-danger mr-2"></i>
            Event End Date & Time *
          </label>
            <Flatpickr
                options={{
                    enableTime: true,
                    dateFormat: 'Y-m-d H:i',
                    minDate: formData.event_start || 'today',
                }}
                value={formData.event_end}
                onChange={([date]) =>
                    setFormData({ ...formData, event_end: formatDate(date) })
                }
                className="form-control"
                placeholder="Event end date & time"
                required
            />
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label font-weight-bold">
            <i className="fas fa-shopping-cart text-info mr-2"></i>
            Ticket Sale Start *
          </label>
            <Flatpickr
                options={{
                    enableTime: true,
                    dateFormat: 'Y-m-d H:i',
                    maxDate: formData.event_start || null,
                }}
                value={formData.sale_start}
                onChange={([date]) =>
                    setFormData({ ...formData, sale_start: formatDate(date) })
                }
                className="form-control"
                placeholder="Ticket sale start"
                required
            />
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label font-weight-bold">
            <i className="fas fa-shopping-cart text-warning mr-2"></i>
            Ticket Sale End *
          </label>
            <Flatpickr
                options={{
                    enableTime: true,
                    dateFormat: 'Y-m-d H:i',
                    minDate: formData.sale_start || null,
                    maxDate: formData.event_start || null,
                }}
                value={formData.sale_end}
                onChange={([date]) =>
                    setFormData({ ...formData, sale_end: formatDate(date) })
                }
                className="form-control"
                placeholder="Ticket sale end"
                required
            />
        </div>

          <div className="col-12 mb-4">
              <div className="card shadow-sm">
                  <div className="card-header py-3 !text-black">
                      <h6 className="m-0 !text-black font-weight-bold">
                          <i className="fas !text-black fa-video mr-2"></i>
                          Upload Event Video (Optional)
                      </h6>
                  </div>

                  <div className="card-body">
                      {existingVideo && !selectedVideo && (
                          <div className="mb-3">
                              <video
                                  src={existingVideo}
                                  controls
                                  className="w-100 rounded shadow-sm"
                                  style={{ maxHeight: '300px' }}
                              />
                              <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm mt-2"
                                  onClick={() => {
                                      setExistingVideo(null);
                                      setRemoveVideoFlag(true); // important
                                      setSelectedVideo(null); // optional safety
                                  }}
                              >
                                  <i className="fas fa-trash-alt mr-1"></i>
                                  Remove existing video
                              </button>
                          </div>
                      )}
                      {/* Drag & Drop Area */}
                      {!existingVideo && !selectedVideo && (
                          <div
                              className="border-dashed !border-purple-500 !bg-purple-50 text-center py-5 mb-3"
                              style={{
                                  border: '3px dashed #4e73df',
                                  borderRadius: '10px',
                                  backgroundColor: '#e3f2fd',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease',
                              }}
                              onClick={() => document.getElementById('eventVideo').click()}
                              onDragOver={(e) => {
                                  e.preventDefault();
                                  e.currentTarget.style.backgroundColor = '#bbdefb';
                              }}
                              onDragLeave={(e) => {
                                  e.preventDefault();
                                  e.currentTarget.style.backgroundColor = '#e3f2fd';
                              }}
                              onDrop={(e) => {
                                  e.preventDefault();
                                  e.currentTarget.style.backgroundColor = '#e3f2fd';
                                  const files = Array.from(e.dataTransfer.files);
                                  handleVideoSelect({ target: { files } });
                              }}
                          >
                              <input
                                  type="file"
                                  id="eventVideo"
                                  name="video_link"
                                  accept="video/*"
                                  onChange={handleVideoSelect}
                                  style={{ display: 'none' }}
                              />

                              <i className="fas fa-cloud-upload-alt fa-3x text-purple-500 mb-3"></i>

                              <h5 className="font-weight-bold mb-2">
                                  Drop video here or click to upload
                              </h5>

                              <p className="text-muted mb-0">
                                  Supports: MP4, MOV, AVI, WebM (Max 50MB)
                              </p>
                          </div>
                      )}

                      {/* Selected Video Info */}
                      {selectedVideo && (
                          <div className="alert !border !border-purple-500 !text-gray-900 d-flex justify-content-between align-items-center">
                              <div>
                                  <i className="fas fa-check-circle mr-2"></i>
                                  <strong>{selectedVideo.name}</strong>
                                  <div className="text-muted small">
                                      {(selectedVideo.size / (1024 * 1024)).toFixed(2)} MB
                                  </div>
                              </div>

                              <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm mt-2"
                                  onClick={() => {
                                      setSelectedVideo(null);  // remove new video
                                      setExistingVideo(null);  // remove old preview
                                      setRemoveVideoFlag(true); // mark for deletion
                                  }}
                              >
                                  <i className="fas fa-trash-alt mr-1"></i>
                                  Remove existing video
                              </button>
                          </div>
                      )}

                      {/* Info */}
                      <small className="text-muted">
                          <i className="fas fa-info-circle mr-1"></i>
                          Upload a promotional video for your event (optional).
                      </small>
                  </div>
              </div>
          </div>

          <div className="col-12">
          <div className="card bg-light">
            <div className="card-body">
              <h6 className="font-weight-bold mb-3">
                <i className="fas fa-cog text-secondary mr-2"></i>
                Event Settings
              </h6>
              <div className="row">
                <div className="col-md-4">
                  <div className="custom-control custom-checkbox">
                      <input
                          type="checkbox"
                          id="featured_event"
                          name="featured_event"
                          className="custom-control-input"
                          checked={formData.featured_event}
                          disabled={isEditMode && originalFeaturedEvent}
                          onChange={handleInputChange}
                      />
                      <label className="custom-control-label" htmlFor="featured_event">
                      <i className="fas fa-star text-warning mr-1"></i>
                      Featured Event
                    </label>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="custom-control custom-checkbox">
                      <input
                          type="checkbox"
                          id="top_event"
                          name="top_event"
                          className="custom-control-input"
                          checked={formData.top_event}
                          disabled={isEditMode && originalTopEvent}
                          onChange={handleInputChange}
                      />
                      <label className="custom-control-label" htmlFor="top_event">
                      <i className="fas fa-trophy text-warning mr-1"></i>
                      Top Event
                    </label>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="custom-control custom-checkbox">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="status"
                      name="status"
                      checked={formData.status}
                      onChange={handleInputChange}
                    />
                    <label className="custom-control-label" htmlFor="status">
                      <i className="fas fa-toggle-on text-success mr-1"></i>
                      Active
                    </label>
                  </div>
                </div>
              </div>

              {/* Payment Notice */}
                {getAddonPrice() > 0 && (
                    <div className="alert alert-info mt-3 mb-0">
                        <i className="fas fa-info-circle mr-2"></i>
                        <strong className="pr-2">Payment Required:</strong> You will be charged
                        <strong className="px-2"> {getAddonPrice()} QAR</strong> for selected promotions.
                    </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Step 2: Location
  const renderLocation = () => (
    <div>
      <div className="row">
        <div className="col-12 mb-3">
          <label className="form-label font-weight-bold">
            <i className="fas fa-map-marked-alt text-primary mr-2"></i>
            Location Address *
          </label>
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            className="form-control form-control-lg"
            placeholder="Enter the full address of your event"
            required
          />
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label font-weight-bold">
            <i className="fas fa-map-pin text-danger mr-2"></i>
            Latitude
          </label>
          <input
            type="text"
            name="latitude"
            value={formData.latitude}
            onChange={handleInputChange}
            className="form-control"
            readOnly
          />
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label font-weight-bold">
            <i className="fas fa-map-pin text-danger mr-2"></i>
            Longitude
          </label>
          <input
            type="text"
            name="longitude"
            value={formData.longitude}
            onChange={handleInputChange}
            className="form-control"
            readOnly
          />
        </div>

        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header py-3  text-white">
              <h6 className="m-0 text-gray-900 font-weight-bold">
                <i className="fas fa-map-marked text-gray-900 mr-2"></i>
                Click on the map to set event location
              </h6>
            </div>
            <div className="card-body p-0">
              <div style={{ height: '500px', width: '100%' }}>
                <Map
                  initialViewState={{
                    longitude: parseFloat(formData.longitude),
                    latitude: parseFloat(formData.latitude),
                    zoom: 13
                  }}
                  style={{ width: '100%', height: '100%' }}
                  mapStyle="mapbox://styles/mapbox/streets-v12"
                  mapboxAccessToken={MAPBOX_TOKEN}
                  onClick={handleMapClick}
                >
                  <Marker
                    longitude={parseFloat(formData.longitude)}
                    latitude={parseFloat(formData.latitude)}
                    color="red"
                  />
                  <NavigationControl position="top-right" />
                </Map>
              </div>
            </div>
            <div className="card-footer bg-light">
              <small className="text-muted">
                <i className="fas fa-info-circle mr-1"></i>
                Click anywhere on the map to pin your event location. You can zoom and pan to find the exact spot.
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Step 3: Tickets
  const renderTickets = () => (
    <div>
      {/* Ticketing mode toggle */}
      <div className="row mb-4">
        <div className="col-md-6 mb-3 mb-md-0">
          <div
            onClick={() => setTicketingMode('tickets')}
            style={{
              cursor: 'pointer',
              border: `2px solid ${ticketingMode === 'tickets' ? '#0d6efd' : '#dee2e6'}`,
              borderRadius: 10,
              padding: '16px 20px',
              background: ticketingMode === 'tickets' ? '#eff6ff' : '#fff',
              transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', gap: 14
            }}
          >
            <i className="fas fa-ticket-alt fa-2x" style={{ color: ticketingMode === 'tickets' ? '#0d6efd' : '#adb5bd' }} />
            <div>
              <div className="font-weight-bold" style={{ color: ticketingMode === 'tickets' ? '#0d6efd' : '#495057' }}>Ticket System</div>
              <small className="text-muted">Create and sell ticket categories</small>
            </div>
            {ticketingMode === 'tickets' && (
              <i className="fas fa-check-circle ml-auto" style={{ color: '#0d6efd', fontSize: 20 }} />
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div
            onClick={() => setTicketingMode('seatmap')}
            style={{
              cursor: 'pointer',
              border: `2px solid ${ticketingMode === 'seatmap' ? '#0d6efd' : '#dee2e6'}`,
              borderRadius: 10,
              padding: '16px 20px',
              background: ticketingMode === 'seatmap' ? '#eff6ff' : '#fff',
              transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', gap: 14
            }}
          >
            <i className="fas fa-map-marked-alt fa-2x" style={{ color: ticketingMode === 'seatmap' ? '#0d6efd' : '#adb5bd' }} />
            <div>
              <div className="font-weight-bold" style={{ color: ticketingMode === 'seatmap' ? '#0d6efd' : '#495057' }}>Seat Map</div>
              <small className="text-muted">Add a seat map after creating the event</small>
            </div>
            {ticketingMode === 'seatmap' && (
              <i className="fas fa-check-circle ml-auto" style={{ color: '#0d6efd', fontSize: 20 }} />
            )}
          </div>
        </div>
      </div>

      {/* Ticket categories section */}
      <div style={{ opacity: ticketingMode === 'seatmap' ? 0.4 : 1, pointerEvents: ticketingMode === 'seatmap' ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
        {ticketingMode === 'seatmap' && (
          <div className="alert alert-info mb-3">
            <i className="fas fa-info-circle mr-2"></i>
            Ticket categories are not required. You can add a seat map to this event after it's created.
          </div>
        )}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="font-weight-bold text-gray-900 mb-0"></h4>
          <button
            type="button"
            onClick={addTicketCategory}
            className="btn !bg-purple-500 !text-white !rounded-lg btn-sm hover:!bg-purple-600"
          >
            <i className="fas fa-plus mr-2"></i>
            Add Category
          </button>
        </div>

      {ticketCategories.map((ticket, index) => (
        <div key={index} className="card shadow-sm mb-3">
          <div className="card-header py-3  text-white d-flex justify-content-between align-items-center">
            <h6 className="m-0 !text-black font-weight-bold">
              <i className="fas fa-ticket-alt mr-2"></i>
              Ticket Category #{index + 1}
            </h6>
            {ticketCategories.length > 1 && (
              <button
                type="button"
                onClick={() => removeTicketCategory(index)}
                className="btn btn-sm btn-danger"
              >
                <i className="fas fa-trash"></i>
              </button>
            )}
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label font-weight-bold">
                  <i className="fas fa-tag text-primary mr-2"></i>
                  Ticket Name *
                </label>
                <input
                  type="text"
                  value={ticket.category_name}
                  onChange={(e) => handleTicketChange(index, 'category_name', e.target.value)}
                  className="form-control"
                  placeholder="e.g., VIP, General Admission, Early Bird"
                  required
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label font-weight-bold">
                  <i className="fas fa-money-bill-wave text-success mr-2"></i>
                  Price ({formData.currency}) *
                </label>
                <div className="input-group">
                  <div className="input-group-prepend">
                    <span className="input-group-text">
                      {formData.currency === 'QAR' ? 'QAR' : formData.currency === 'USD' ? '$' : formData.currency === 'AED' ? 'AED' : formData.currency === 'SAR' ? 'SAR' : '€'}
                    </span>
                  </div>
                  <input
                    type="number"
                    value={ticket.price}
                    onChange={(e) => handleTicketChange(index, 'price', e.target.value)}
                    className="form-control"
                    placeholder="99.00"
                    step="0.01"
                    min="0"
                    required
                  />
                </div>
              </div>

              <div className="col-12 mb-3">
                <label className="form-label font-weight-bold">
                  <i className="fas fa-align-left text-info mr-2"></i>
                  Description
                </label>
                <textarea
                  value={ticket.description}
                  onChange={(e) => handleTicketChange(index, 'description', e.target.value)}
                  className="form-control"
                  rows="2"
                  placeholder="Add details about this ticket type..."
                />
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label font-weight-bold">
                  <i className="fas fa-box text-warning mr-2"></i>
                  Available Quantity *
                </label>
                <input
                  type="number"
                  value={ticket.remaining_tickets}
                  onChange={(e) => handleTicketChange(index, 'remaining_tickets', e.target.value)}
                  className="form-control"
                  placeholder="100"
                  min="1"
                  required
                />
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label font-weight-bold">
                  <i className="fas fa-shopping-cart text-info mr-2"></i>
                  Max Per Order
                </label>
                <input
                  type="number"
                  value={ticket.ceiling_limit}
                  onChange={(e) => handleTicketChange(index, 'ceiling_limit', e.target.value)}
                  className="form-control"
                  placeholder="10"
                  min="1"
                />
              </div>

              <div className="col-md-4 mb-3 d-flex align-items-center">
                <div className="custom-control custom-switch">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id={`ticket_status_${index}`}
                    checked={ticket.open_to_public}
                    onChange={(e) => handleTicketChange(index, 'open_to_public', e.target.checked)}
                  />
                  <label className="custom-control-label font-weight-bold" htmlFor={`ticket_status_${index}`}>
                    <i className={`fas fa-${ticket.open_to_public ? 'check-circle text-success' : 'times-circle text-danger'} mr-1`}></i>
                    {ticket.open_to_public ? 'published' : 'not published'}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
      </div>{/* end ticket categories section */}

      {/* Valet Parking Section */}
      <div className="card shadow-sm mt-4" style={{ border: valetEnabled ? '2px solid #8B5CF6' : '2px solid #dee2e6', borderRadius: 12 }}>
        <div className="card-header py-3 d-flex justify-content-between align-items-center" style={{ background: valetEnabled ? '#F5F3FF' : '#f8f9fc', borderRadius: '10px 10px 0 0' }}>
          <h6 className="m-0 font-weight-bold" style={{ color: valetEnabled ? '#7C3AED' : '#6c757d' }}>
            <i className="fas fa-parking mr-2"></i>
            Valet Parking
          </h6>
          <div className="custom-control custom-switch">
            <input
              type="checkbox"
              className="custom-control-input"
              id="valet_enabled"
              checked={valetEnabled}
              onChange={(e) => setValetEnabled(e.target.checked)}
            />
            <label className="custom-control-label font-weight-bold" htmlFor="valet_enabled">
              {valetEnabled ? 'Enabled' : 'Disabled'}
            </label>
          </div>
        </div>
        {valetEnabled && (
          <div className="card-body">
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label font-weight-bold">
                  <i className="fas fa-tag text-primary mr-2"></i>
                  Label
                </label>
                <input
                  type="text"
                  value={valetParking.category_name}
                  onChange={(e) => setValetParking({ ...valetParking, category_name: e.target.value })}
                  className="form-control"
                  placeholder="Valet Parking"
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label font-weight-bold">
                  <i className="fas fa-money-bill-wave text-success mr-2"></i>
                  Price ({formData.currency}) *
                </label>
                <div className="input-group">
                  <div className="input-group-prepend">
                    <span className="input-group-text">
                      {formData.currency === 'QAR' ? 'QAR' : formData.currency === 'USD' ? '$' : formData.currency === 'AED' ? 'AED' : formData.currency === 'SAR' ? 'SAR' : '€'}
                    </span>
                  </div>
                  <input
                    type="number"
                    value={valetParking.price}
                    onChange={(e) => setValetParking({ ...valetParking, price: e.target.value })}
                    className="form-control"
                    placeholder="50.00"
                    step="0.01"
                    min="0"
                    required={valetEnabled}
                  />
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label font-weight-bold">
                  <i className="fas fa-car text-warning mr-2"></i>
                  Parking Spots Available *
                </label>
                <input
                  type="number"
                  value={valetParking.remaining_tickets}
                  onChange={(e) => setValetParking({ ...valetParking, remaining_tickets: e.target.value })}
                  className="form-control"
                  placeholder="50"
                  min="1"
                  required={valetEnabled}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label font-weight-bold">
                  <i className="fas fa-shopping-cart text-info mr-2"></i>
                  Max Per Order
                </label>
                <input
                  type="number"
                  value={valetParking.ceiling_limit}
                  onChange={(e) => setValetParking({ ...valetParking, ceiling_limit: e.target.value })}
                  className="form-control"
                  placeholder="2"
                  min="1"
                />
              </div>
              <div className="col-12 mb-0">
                <label className="form-label font-weight-bold">
                  <i className="fas fa-align-left text-info mr-2"></i>
                  Description
                </label>
                <textarea
                  value={valetParking.description}
                  onChange={(e) => setValetParking({ ...valetParking, description: e.target.value })}
                  className="form-control"
                  rows="2"
                  placeholder="Valet parking available at the main entrance..."
                />
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );

  // Step 4: Images
  const renderImages = () => (
    <div>
      <div className="card shadow-sm mb-4">
        <div className="card-header py-3 text-gray-900">
          <h6 className="m-0 font-weight-bold !text-black">
            <i className="fas fa-cloud-upload-alt  mr-2"></i>
            Upload Event Images
          </h6>
        </div>
        <div className="card-body">
          {/* Drag & Drop Upload Area */}
          <div 
            className="border-dashed bg-white !border-purple-500 text-center py-5 mb-3"
            style={{
              border: '3px dashed #4e73df',
              borderRadius: '10px',
              backgroundColor: existingImages.length + selectedImages.length >= 5 ? '#f8f9fc' : '#e3f2fd',
              cursor: existingImages.length + selectedImages.length >= 5 ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease'
            }}
            onClick={() => {
              if (existingImages.length + selectedImages.length < 5) {
                document.getElementById('eventImages').click();
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.style.backgroundColor = '#bbdefb';
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.currentTarget.style.backgroundColor = '#e3f2fd';
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.style.backgroundColor = '#e3f2fd';
              if (existingImages.length + selectedImages.length < 5) {
                const files = Array.from(e.dataTransfer.files);
                const event = { target: { files } };
                handleImageSelect(event);
              }
            }}
          >
            <input
              type="file"
              id="eventImages"
              multiple
              accept="image/*"
              onChange={handleImageSelect}
              disabled={existingImages.length + selectedImages.length >= 5}
              style={{ display: 'none' }}
            />
            <i className="fas fa-cloud-upload-alt fa-3x text-purple-500 mb-3"></i>
            <h5 className="text-purple-500 font-weight-bold mb-2">
              {existingImages.length + selectedImages.length >= 5 
                ? 'Maximum Images Reached' 
                : 'Drop images here or click to upload'}
            </h5>
            <p className="text-muted mb-0">
              <i className="fas fa-info-circle mr-1"></i>
              Supports: JPG, PNG, GIF, WebP (Max 10MB each)
            </p>
            <p className="text-muted small mb-0 mt-2">
              Recommended: 1920x1080px for best quality
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="font-weight-bold text-muted">Upload Progress</small>
              <small className="font-weight-bold text-purple-500">
                {existingImages.length + selectedImages.length} / 5 images
              </small>
            </div>
            <div className="progress" style={{ height: '20px' }}>
              <div
                className={`progress-bar progress-bar-striped ${existingImages.length + selectedImages.length >= 5 ? 'bg-success' : '!bg-purple-500'}`}
                role="progressbar"
                style={{ width: `${((existingImages.length + selectedImages.length) / 5) * 100}%` }}
              >
                {Math.round(((existingImages.length + selectedImages.length) / 5) * 100)}%
              </div>
            </div>
          </div>

          {/* Image Count Info */}
          <div className=" pt-2 mb-0">
            <i className="fas fa-lightbulb mr-2"></i>
            <strong>Pro Tip:</strong> Upload multiple angles and high-quality images to make your event stand out!
            {existingImages.length + selectedImages.length < 5 && (
              <span> You can add <strong>{5 - (existingImages.length + selectedImages.length)}</strong> more image(s).</span>
            )}
          </div>
        </div>
      </div>

      {/* Existing Images */}
      {existingImages.length > 0 && (
        <div className="mb-4">
          <div className="card shadow-sm border-left-success">
            <div className="card-header py-2 bg-light">
              <h6 className="m-0 font-weight-bold text-success">
                <i className="fas fa-check-circle mr-2"></i>
                Current Images ({existingImages.length})
              </h6>
            </div>
            <div className="card-body">
              <div className="row">
                {existingImages.map((image, index) => (
                  <div key={image.id} className="col-md-4 col-lg-3 mb-3">
                    <div className="card shadow-sm h-100 position-relative" style={{ overflow: 'hidden' }}>
                      {index === 0 && (
                        <span className="badge badge-primary position-absolute" style={{ top: '10px', left: '10px', zIndex: 1 }}>
                          <i className="fas fa-star mr-1"></i>Primary
                        </span>
                      )}
                      <img
                        src={image.image_url}
                        alt="Event"
                        className="card-img-top"
                        style={{ height: '180px', objectFit: 'cover' }}
                      />
                      <div className="card-body p-2 bg-light">
                        <button
                          type="button"
                          onClick={() => removeExistingImage(image.id)}
                          className="btn btn-danger btn-sm btn-block"
                          style={{ fontSize: '0.8rem' }}
                        >
                          <i className="fas fa-trash-alt mr-1"></i>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Images */}
      {imagePreviews.length > 0 && (
        <div className="mb-4">
          <div className="card shadow-sm border-left-purple-500">
            <div className="card-header py-2 bg-light">
              <h6 className="m-0 font-weight-bold !text-black">
                <i className="fas fa-plus-circle mr-2"></i>
                New Images to Upload ({imagePreviews.length})
              </h6>
            </div>
            <div className="card-body">
              <div className="row">
                {imagePreviews.map((preview, index) => (
                  <div key={index} className="col-md-4 col-lg-3 mb-3">
                    <div className="card shadow-sm h-100 border-primary position-relative" style={{ overflow: 'hidden' }}>
                      <img
                        src={preview}
                        alt="Preview"
                        className="card-img-top"
                        style={{ height: '180px', objectFit: 'cover' }}
                      />
                      <div className="card-body p-2 bg-light">
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="btn btn-warning btn-sm btn-block"
                          style={{ fontSize: '0.8rem' }}
                        >
                          <i className="fas fa-times mr-1"></i>
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {(existingImages.length + imagePreviews.length === 0) && (
        <div className="card shadow-sm mb-4">
          <div className="card-body text-center py-5">
            <div className="text-purple-500 mb-3" style={{ fontSize: '80px' }}>
              <i className="fas fa-images"></i>
            </div>
            <h4 className="text-muted mb-2">No images uploaded yet</h4>
            <p className="text-muted mb-0">
              <i className="fas fa-arrow-up mr-1"></i>
              Use the upload area above to add stunning images to your event
            </p>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
      return (
          <div className="flex items-center justify-center min-h-screen">
              <div className="text-center">
                  <svg className="animate-spin h-12 w-12 mx-auto text-purple-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="mt-4 text-gray-600">Loading...</p>
              </div>
          </div>
      );
  }

  return (
    <div className="container-fluid">
      {/* Page Heading */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h5 className="h3 mb-0 font-weight-bold text-gray-800">
          <i className={`fas ${isEditMode ? 'fa-edit' : 'fa-plus-circle'} mr-2`}></i>
          {isEditMode ? 'Edit Event' : 'Create New Event'}
        </h5>
      </div>

      {/* Wizard Card */}
      <div className="card shadow mb-4">
        <div className="card-body">
          {/* Step Indicator */}
          <div className="mb-4">
            <div className="d-flex align-items-center justify-content-between position-relative">
              {/* Progress Line — inset to align with icon centres (each col = 25%, centre = 12.5% = 1/8) */}
              <div className="position-absolute" style={{ top: '30px', left: 'calc(100% / 8)', right: 'calc(100% / 8)', zIndex: 0 }}>
                <div className="progress" style={{ height: '4px' }}>
                  <div
                    className="progress !bg-success"
                    role="progressbar"
                    style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Steps */}
              {steps.map((step, index) => (
                <div key={step.number} className="text-center position-relative" style={{ flex: 1, zIndex: 1 }}>
                  <div
                    className={`d-inline-flex align-items-center justify-content-center rounded-circle mb-2 ${
                      currentStep === step.number
                        ? '!bg-purple-500 text-white shadow'
                        : currentStep > step.number
                        ? 'bg-success text-white'
                        : 'bg-light text-muted border'
                    }`}
                    style={{ width: '60px', height: '60px', fontSize: '24px' }}
                  >
                    <i className={`fas ${currentStep > step.number ? 'fa-check' : step.icon}`}></i>
                  </div>
                  <div>
                    <small className={`font-weight-bold ${
                      currentStep === step.number
                        ? '!text-black'
                        : currentStep > step.number
                        ? 'text-black'
                        : 'text-muted'
                    }`}>
                      {step.title}
                    </small>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <hr className="my-4" />

          {/* Current Step Indicator */}
          {/*<div className="alert alert-warning mb-3">*/}
          {/*  <strong>*/}
          {/*    <i className="fas fa-info-circle mr-2"></i>*/}
          {/*    Step {currentStep} of 4: {steps[currentStep - 1].title}*/}
          {/*  </strong>*/}
          {/*</div>*/}

          <form 
            onSubmit={handleSubmit}
            onKeyDown={(e) => {
              // Prevent Enter key from submitting form on steps 1-3
              if (e.key === 'Enter' && currentStep !== 4) {
                e.preventDefault();
                console.log('Enter key blocked on step', currentStep);
              }
            }}
          >
            {/* Render current step */}
            {currentStep === 1 && renderBasicInfo()}
            {currentStep === 2 && renderLocation()}
            {currentStep === 3 && renderTickets()}
            {currentStep === 4 && renderImages()}

            {/* Navigation Buttons */}
            <hr className="my-4" />
            <div className="d-flex justify-content-between">
              <button
                type="button"
                onClick={prevStep}
                disabled={currentStep === 1}
                className="btn btn-secondary !text-white border-0"
                style={{ minWidth: '120px' }}
              >
                <i className="fas fa-arrow-left mr-2"></i>
                Previous
              </button>

              {currentStep < 4 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="btn !bg-purple-500 !text-white hover:!bg-purple-600"
                  style={{ minWidth: '120px' }}
                >
                  Next
                  <i className="fas fa-arrow-right ml-2"></i>
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  className={`btn ${
                    // Check if payment is required for both creation and update
                    (!isEditMode && (formData.featured_event || formData.top_event)) || 
                    (isEditMode && ((!originalFeaturedEvent && formData.featured_event) || (!originalTopEvent && formData.top_event)))
                    ? '!bg-purple-500 !text-white hover:!bg-purple-600' 
                    : 'btn-success'
                  }`}
                  style={{ minWidth: '150px' }}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                      {
                        // Check if payment is required
                        (!isEditMode && (formData.featured_event || formData.top_event)) ||
                        (isEditMode && ((!originalFeaturedEvent && formData.featured_event) || (!originalTopEvent && formData.top_event)))
                        ? 'Processing...'
                        : isEditMode ? 'Saving...' : 'Creating...'
                      }
                    </>
                  ) : (
                    <>
                      <i className={`fas ${
                        // Check if payment is required
                        (!isEditMode && (formData.featured_event || formData.top_event)) || 
                        (isEditMode && ((!originalFeaturedEvent && formData.featured_event) || (!originalTopEvent && formData.top_event)))
                        ? 'fa-credit-card' 
                        : isEditMode ? 'fa-save' : 'fa-check'
                      } mr-2`}></i>
                      {
                        // Check if payment is required
                        (!isEditMode && (formData.featured_event || formData.top_event)) ||
                        (isEditMode && ((!originalFeaturedEvent && formData.featured_event) || (!originalTopEvent && formData.top_event)))
                        ? 'Proceed to Payment'
                        : isEditMode ? 'Save Changes' : 'Create Event'
                      }
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateEvent;

